import { Injectable } from '@angular/core';
import { FeatureId } from '@weavix/models/src/features/feature';
import { PermissionAction } from 'weavix-shared/permissions/permissions.model';
import { FeatureFlagService } from './feature-flag.service';
import { ProfileService } from './profile.service';

/** The actions that gate location history reads on the server once `StrictLocationReadPermissions` is on. */
export type LocationHistoryAction = PermissionAction.ViewBacktrack | PermissionAction.ViewGeofenceHistory;

/** Decides whether location history UI shows, matching the server's strict location read checks. */
@Injectable({
    providedIn: 'root',
})
export class LocationHistoryAccessService {
    constructor(
        private readonly featureFlagService: FeatureFlagService,
        private readonly profileService: ProfileService,
    ) {}

    /**
     * Whether the user may use the history feature behind `action` at a site: always while the account's
     * `StrictLocationReadPermissions` flag is off, otherwise only when their group grants `action` there.
     */
    async canView(action: LocationHistoryAction, accountId: string, facilityId: string): Promise<boolean> {
        if (!await this.isStrict(accountId)) return true;
        return this.profileService.hasPermission(action, facilityId);
    }

    /** Reads the account-level flag; a failed read is logged and counts as off, as it does on the server. */
    private async isStrict(accountId: string): Promise<boolean> {
        try {
            return await this.featureFlagService.isFacilityFeatureFlagEnabled(FeatureId.StrictLocationReadPermissions, accountId, []);
        } catch (e) {
            console.error(`Failed to read the ${FeatureId.StrictLocationReadPermissions} feature flag`, { accountId }, e);
            return false;
        }
    }
}
